import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Grade } from '@cfl/shared';

import { api, type ApiError } from '@/lib/api';

export interface AnnualReview {
  id: string | null;
  employeeId: string;
  employeeName: string;
  cycleId: string;
  cycleLabel: string;
  quarterRatings: Array<{ index: number; rating: number | null }>;
  /** Mean of the completed quarters. Recomputed server-side on every read. */
  suggestedRating: number | null;
  suggestedGrade: Grade | null;
  finalRating: number | null;
  finalGrade: Grade | null;
  overallFeedback: string | null;
  promotionRecommended: boolean;
  oneOnOneId: string | null;
  oneOnOneAcknowledged: boolean;
  reviewedByName: string | null;
  completedAt: string | null;
  windowOpen: boolean;
  canFinalise: boolean;
}

export interface AnnualReviewRow {
  employeeId: string;
  employeeName: string;
  departmentName: string | null;
  suggestedRating: number | null;
  finalRating: number | null;
  finalGrade: Grade | null;
  promotionRecommended: boolean;
  oneOnOneAcknowledged: boolean;
  completedAt: string | null;
}

export function useAnnualReviews(enabled = true) {
  return useQuery<AnnualReviewRow[], ApiError>({
    queryKey: ['annual-reviews'],
    queryFn: () => api.get<AnnualReviewRow[]>('/annual-reviews'),
    enabled,
  });
}

export function useAnnualReview(employeeId: string | 'me') {
  return useQuery<AnnualReview, ApiError>({
    queryKey: ['annual-review', employeeId],
    queryFn: () =>
      api.get<AnnualReview>(
        employeeId === 'me' ? '/annual-reviews/me' : `/annual-reviews/employee/${employeeId}`,
      ),
  });
}

export function useFinaliseAnnualReview(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    AnnualReview,
    ApiError,
    {
      finalRating: number;
      overallFeedback?: string | null;
      promotionRecommended?: boolean;
    }
  >({
    mutationFn: (body) =>
      api.post<AnnualReview>(`/annual-reviews/employee/${employeeId}/finalise`, body),
    onSuccess: (review) => {
      queryClient.setQueryData(['annual-review', employeeId], review);
      void queryClient.invalidateQueries({ queryKey: ['annual-reviews'] });
    },
  });
}
